"use client";

import type React from "react";
import { useState, useMemo } from "react";
import {
  FormControl,
  FormLabel,
  Input,
  VStack,
  Box,
  Text,
} from "@chakra-ui/react";
import { TIMEZONES } from "../../constants/timezones";

interface TimezoneSelectProps {
  value: string;
  onChange: (timezone: string) => void;
  label?: string;
  isRequired?: boolean;
  placeholder?: string;
}

export const TimezoneSelect: React.FC<TimezoneSelectProps> = ({
  value,
  onChange,
  label = "Timezone",
  isRequired = false,
  placeholder = "Search or select timezone...",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const filteredTimezones = useMemo(() => {
    if (!searchTerm) return TIMEZONES;
    const lowerSearch = searchTerm.toLowerCase();
    return TIMEZONES.filter((tz) => tz.toLowerCase().includes(lowerSearch));
  }, [searchTerm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);
    onChange(inputValue);
    setIsOpen(true);
  };

  const handleSelectTimezone = (timezone: string) => {
    onChange(timezone);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleInputFocus = () => setIsOpen(true);

  const handleInputBlur = () => {
    // Delay to allow click on dropdown item
    setTimeout(() => setIsOpen(false), 200);
  };

  const blueFocus = {
    borderColor: "blue.500",
    boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
  };

  return (
    <FormControl isRequired={isRequired}>
      <FormLabel color="gray.800">{label}</FormLabel>

      <Box position="relative">
        <Input
          value={searchTerm || value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          variant="outline"
          borderColor="blue.300"
          focusBorderColor="blue.400"
          _placeholder={{ color: "gray.500" }}
          _hover={{ borderColor: "blue.400" }}
          _focus={blueFocus}
          bg="white"
          autoComplete="off"
        />

        {isOpen && filteredTimezones.length > 0 && (
          <VStack
            position="absolute"
            top="100%"
            left={0}
            right={0}
            mt={1}
            bg="white"
            border="1px solid"
            borderColor="blue.200"
            borderRadius="md"
            maxH="200px"
            overflowY="auto"
            zIndex={1000}
            spacing={0}
            align="stretch"
            boxShadow="md"
            sx={{
              scrollbarWidth: "thin",
              scrollbarColor: "var(--chakra-colors-blue-400) transparent",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "var(--chakra-colors-blue-400)",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "var(--chakra-colors-blue-500)",
              },
            }}
          >
            {filteredTimezones.slice(0, 100).map((timezone) => {
              const isSelected = value === timezone;
              return (
                <Box
                  key={timezone}
                  px={3}
                  py={2}
                  cursor="pointer"
                  onMouseDown={() => handleSelectTimezone(timezone)}
                  bg={isSelected ? "blue.50" : "white"}
                  _hover={{ bg: "blue.50" }}
                  borderLeft={
                    isSelected ? "3px solid" : "3px solid transparent"
                  }
                  borderLeftColor={isSelected ? "blue.400" : "transparent"}
                >
                  <Text fontSize="sm" color="gray.800">
                    {timezone}
                  </Text>
                </Box>
              );
            })}

            {filteredTimezones.length > 100 && (
              <Box px={3} py={2} bg="blue.50">
                <Text fontSize="xs" color="gray.600">
                  Showing first 100 results. Keep typing to narrow down...
                </Text>
              </Box>
            )}
          </VStack>
        )}

        {isOpen && filteredTimezones.length === 0 && searchTerm && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            mt={1}
            bg="white"
            border="1px solid"
            borderColor="blue.200"
            borderRadius="md"
            p={3}
            zIndex={1000}
            boxShadow="md"
          >
            <Text fontSize="sm" color="gray.500">
              No timezones found
            </Text>
          </Box>
        )}
      </Box>
    </FormControl>
  );
};
